import React, { useState, useEffect } from 'react';
import { injectIntl, FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import {
  getTranslatedText,
  B3,
  Loader,
  getHeaderValue,
  trimInputValues,
  withErrorBoundary,
  useIntl,
  fetchApi,
  Editor,
  Row
} from '@nile/components';
import { SelectUsers } from '@nile/mangifera/src/Pebbles/ChatContainer';
import {
  GET_CLIENT_USERS,
  AccountClient,
  GET_CONTENT_FOR_SEND_INVOICE
} from '@nile/graphql';
import { isEmpty, pick, cloneDeep } from 'lodash';
import { HiddenActions } from '@nile/client_management/src/Pebbles/EmailTemplate';
import { TextField, ButtonGroup, Button } from '@superops/ui-design-system';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import envConfig from '@nile/home/src/Config/env';

function AttachPdfPreview({ displayId, invoiceFileNameSuffix }) {
  return (
    <div className="br-5 flex center g10 bg-white border p-5-10">
      <div>
        <SVGIcon name="PdfFile" color="#CF4E45" />
      </div>
      <div className="invoice-text font-weight-500 fsize12 text-black">
        {invoiceFileNameSuffix
          ? `invoice_${invoiceFileNameSuffix}_${displayId}.pdf`
          : `invoice_${displayId}.pdf`}
      </div>
    </div>
  );
}

const SendEmailModal = (props) => {
  const intl = useIntl();
  const { invoiceId, accountId, attachPdf, displayId } = props;
  const [template, setTemplate] = useState({
    subject: '',
    content: '',
    attachments: []
  });
  const [isUploading, setIsUploading] = useState(false);
  const [toUsers, setToUsers] = useState([]);
  const [ccUsers, setCcUsers] = useState([]);
  const [bccUsers, setBccUsers] = useState([]);
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [contentErrors, setContentErrors] = useState(null);

  const updateTemplate = (obj) => {
    const updatedTemplate = cloneDeep(template);
    setTemplate({ ...updatedTemplate, ...obj });
  };

  const updateUsers = (section, val) => {
    const updatedTemplate = cloneDeep(template);
    // setTemplate({ ...updatedTemplate, ...obj });
    switch (section) {
      case 'toUsers':
        setToUsers(val);
        break;
      case 'ccUsers':
        setCcUsers(val);
        break;
      case 'bccUsers':
        setBccUsers(val);
        break;
      default:
    }

    if (isEmpty(val)) {
      updateTemplate({ ...updatedTemplate, [section]: [] });
      return;
    }
    const clonedVal = val.slice();
    const recepientArray = [];
    clonedVal.forEach((obj) => {
      if (obj?.userId) {
        const parsedObj = pick(obj, [
          'userId',
          'firstName',
          'lastName',
          'email'
        ]);
        recepientArray.push({ ...parsedObj });
      } else if (obj.__isNew__ || obj.isEmail) {
        // react-select returns isNew for newly created items in dropdown.
        recepientArray.push({ email: obj.value });
      }
    });
    updateTemplate({ ...updatedTemplate, [section]: recepientArray });
  };

  const updateState = (section, state) => {
    switch (section) {
      case 'showCc':
        setShowCc(state);
        break;
      case 'showBcc':
        setShowBcc(state);
        break;
      default:
    }
  };

  const handleFileAttachments = (files) => {
    const formData = new FormData();
    files.forEach((file, idx) => {
      formData.append(`files`, file);
    });
    const details = {
      clientId: accountId
    };
    if (accountId) {
      formData.append('details', JSON.stringify(details));
    }
    formData.append('module', 'CLIENT_CONVERSATION_ATTACHMENT');

    const requestOptions = {
      method: 'POST',
      credentials: 'include',
      body: formData,
      headers: getHeaderValue()
    };
    setIsUploading(true);
    fetchApi(`${envConfig.fileRoot}/upload`, requestOptions)
      .then((response) => {
        return response.json();
      })
      .then((list) => {
        setIsUploading(false);
        const updatedTemplate = cloneDeep(template);
        const updatedList = [...updatedTemplate?.attachments, ...list.data];
        updatedTemplate.attachments =
          updatedList?.map((el) => {
            return {
              fileName: el.fileName,
              fileSize: el.fileSize,
              originalFileName: el.originalFileName
            };
          }) || [];
        setTemplate(updatedTemplate);
      })
      .catch((err) => {
        setIsUploading(false);
      });
  };

  const handleAttachmentsDeletion = (fileName) => {
    const updatedTemplate = cloneDeep(template);
    const updatedList = cloneDeep(updatedTemplate?.attachments).filter(
      (item) => item.fileName !== fileName
    );
    updatedTemplate.attachments = updatedList;
    setTemplate(updatedTemplate);
  };

  const validateTemplate = (params) => {
    const trimmedValues = trimInputValues(params);
    let errors = null;
    if (!trimmedValues?.subject) {
      const subjectError = {
        subject: getTranslatedText(intl, 'common.subject.error')
      };
      errors = errors ? { ...errors, ...subjectError } : subjectError;
    }

    if (
      (trimmedValues?.toUsers && !trimmedValues?.toUsers.length) ||
      !trimmedValues?.toUsers
    ) {
      const toUsersError = {
        toUsersError: getTranslatedText(intl, 'common.toUsers.error')
      };
      errors = errors ? { ...errors, ...toUsersError } : toUsersError;
    }
    return errors;
  };

  const sendContent = () => {
    const validationError = validateTemplate(template);
    if (validationError) {
      setContentErrors(validationError);
    } else {
      setContentErrors(null);
      props.primaryAction(template);
    }
  };

  const {
    data: sendInvoiceContent,
    loading: invoiceContentLoading,
    error: sendInvoiceContentError
  } = useQuery(GET_CONTENT_FOR_SEND_INVOICE, {
    client: AccountClient,
    variables: {
      input: {
        invoiceId: invoiceId
      }
    },
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (sendInvoiceContent?.getContentForSendInvoice) {
      const toUsersArray = sendInvoiceContent?.getContentForSendInvoice.toUsers
        ? sendInvoiceContent?.getContentForSendInvoice.toUsers.map((el) => {
            return {
              ...el.user,
              label: el.user.name || el.user.email,
              value: el.user.name || el.user.email
            };
          })
        : [];
      const ccUsersArray = sendInvoiceContent?.getContentForSendInvoice.ccUsers
        ? sendInvoiceContent?.getContentForSendInvoice.ccUsers.map((el) => {
            return {
              ...el.user,
              label: el.user.name || el.user.email,
              value: el.user.name || el.user.email
            };
          })
        : [];
      const bccUsersArray = sendInvoiceContent?.getContentForSendInvoice
        .bccUsers
        ? sendInvoiceContent?.getContentForSendInvoice.bccUsers.map((el) => {
            return {
              ...el.user,
              label: el.user.name || el.user.email,
              value: el.user.name || el.user.email
            };
          })
        : [];
      const data = {
        subject: sendInvoiceContent?.getContentForSendInvoice.subject || '',
        content: sendInvoiceContent?.getContentForSendInvoice.content || '',
        attachments:
          sendInvoiceContent?.getContentForSendInvoice.attachments || [],
        toUsers: sendInvoiceContent?.getContentForSendInvoice.toUsers
          ? sendInvoiceContent?.getContentForSendInvoice.toUsers.map((el) => {
              return {
                ...el.user
              };
            })
          : [],
        ccUsers: sendInvoiceContent?.getContentForSendInvoice.ccUsers
          ? sendInvoiceContent?.getContentForSendInvoice.ccUsers?.map((el) => {
              return {
                ...el.user
              };
            })
          : [],
        bccUsers: sendInvoiceContent?.getContentForSendInvoice.bccUsers
          ? sendInvoiceContent?.getContentForSendInvoice.bccUsers?.map((el) => {
              return {
                ...el.user
              };
            })
          : [],
        conversationId:
          sendInvoiceContent?.getContentForSendInvoice?.conversationId ||
          undefined
      };
      setToUsers(toUsersArray);
      setCcUsers(ccUsersArray);
      setBccUsers(bccUsersArray);
      setShowCc(!!sendInvoiceContent?.getContentForSendInvoice.ccUsers?.length);
      setShowBcc(
        !!sendInvoiceContent?.getContentForSendInvoice.bccUsers?.length
      );
      setTemplate(data);
    }
  }, [sendInvoiceContent]);

  if (invoiceContentLoading) return <Loader />;

  return (
    <ModalWrapper className="flex fcol" attachPdf={attachPdf}>
      <div className="full-width pl40 pr-20 pt20 pb20 title">
        <FormattedMessage id="invoice.send.email.header" />
      </div>
      <Recipients className="full-width pl40 pr-20">
        <B3 className="fw-500">
          {getTranslatedText(intl, 'app.mail.recepients.to')}
        </B3>
        <div className="flex center">
          <div className="fg1">
            <SelectUsers
              updateField={updateUsers.bind(this, 'toUsers')}
              property="userId"
              query={GET_CLIENT_USERS}
              params={{
                client: AccountClient,
                variables: { input: { clientId: accountId } }
              }}
              methodName="getClientUserAssociationsList"
              field="to"
              value={toUsers}
              isMulti
              creatable
              errors={contentErrors?.toUsersError || null}
            />
          </div>
          <HiddenActions
            updateState={updateState}
            showCc={showCc}
            showBcc={showBcc}
            intl={intl}
          />
        </div>
      </Recipients>
      {showCc && (
        <Recipients className="full-width pl40 pr-20">
          <B3 className="fw500">
            {getTranslatedText(intl, 'app.mail.recepients.cc')}
          </B3>
          <div>
            <SelectUsers
              updateField={updateUsers.bind(this, 'ccUsers')}
              property="userId"
              query={GET_CLIENT_USERS}
              params={{
                client: AccountClient,
                variables: { input: { clientId: accountId } }
              }}
              methodName="getClientUserAssociationsList"
              field="cc"
              value={ccUsers}
              isMulti
              creatable
            />
          </div>
        </Recipients>
      )}
      {showBcc && (
        <Recipients className="full-width pl40 pr-20">
          <B3 className="fw500">
            {getTranslatedText(intl, 'app.mail.recepients.bcc')}
          </B3>
          <div>
            <SelectUsers
              updateField={updateUsers.bind(this, 'bccUsers')}
              property="userId"
              query={GET_CLIENT_USERS}
              params={{
                client: AccountClient,
                variables: { input: { clientId: accountId } }
              }}
              methodName="getClientUserAssociationsList"
              field="bcc"
              value={bccUsers}
              isMulti
              creatable
            />
          </div>
        </Recipients>
      )}

      <PrimaryHeader>
        <TextField
          value={template?.subject}
          id="templateName"
          onChange={(val) => {
            updateTemplate({ subject: val });
          }}
          placeholder={getTranslatedText(
            intl,
            'quote.create.sendQuote.subject.placeholder'
          )}
          name="templateName"
          type="text"
          error={contentErrors?.subject || null}
          disabled={props.disableSubjectEdit}
        />
      </PrimaryHeader>

      <div className="f11 scroll">
        <div className="ml-40 mr-40 pb20 fhp">
          {template.content && (
            <Editor
              template={template}
              updateTemplate={updateTemplate}
              handleFileAttachments={handleFileAttachments}
              handleAttachmentsDeletion={handleAttachmentsDeletion}
              placeholder={getTranslatedText(intl, 'common.email.placeholder')}
              module="CLIENT_CONVERSATION"
              details={
                accountId
                  ? { operation: 'CREATE', clientId: accountId }
                  : { operation: 'CREATE' }
              }
              allowMentions={false}
              isUploading={isUploading}
              config={{
                htmlUntouched: true
              }}
            />
          )}
        </div>
      </div>
      {attachPdf && (
        <div className="position-absolute bottom-75 z1">
          <Row gap={10} className="pl10">
            <AttachPdfPreview displayId={displayId} />
            {props.hasUsageReport && (
              <AttachPdfPreview
                displayId={displayId}
                invoiceFileNameSuffix="usage"
              />
            )}
          </Row>
        </div>
      )}

      <div className="w-fit position-absolute right-40 bottom-25 z400">
        <ButtonGroup>
          <Button plainSecondary onClick={props.resetState}>
            <FormattedMessage id="common.cancel" />
          </Button>
          <Button
            primary
            onClick={sendContent}
            loading={props.primaryActionLoading}
            icon={<SVGIcon name="SendWhite" />}
          >
            <FormattedMessage id="app.actions.send" />
          </Button>
        </ButtonGroup>
      </div>
    </ModalWrapper>
  );
};

const ModalWrapper = styled.div`
  background: #fff;
  border-radius: 10px;
  height: 600px;
  min-width: 800px;
  max-width: 800px;
  position: relative;

  .title {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    font-weight: 700;
    font-size: 20px;
  }

  .text-editor {
    display: flex !important;
    flex-direction: column !important;
    height: 100% !important;
    & > div:nth-child(2) {
      padding: ${(props) =>
        props.attachPdf ? '8px 40px 8px 330px ' : '8px 40px'} !important;
      min-height: ${(props) => props.attachPdf && '60px'};
    }

    .quill {
      flex: 1;
      overflow: auto;
    }
  }
  .ql-editor {
    padding: 15px 40px;
    &::before {
      left: 40px !important;
    }
  }
  .ql-container {
    border: none !important;
  }
  .ql-toolbar {
    border: none !important;
    padding: 8px 40px !important;
    margin-bottom: 20px;
    .ql-picker-label {
      padding-left: 0 !important;
    }
  }

  .Superops-InlineError {
    color: #d2332d;
  }

  #templateNameError {
    margin-left: 40px;
  }

  .AttachPdfIcon {
    bottom: 74px;
    height: 44px;
    padding: 0 15px;
    min-width: 160px;
    z-index: 1;
  }
`;

const PrimaryHeader = styled.div`
  .Superops-Connected {
    padding-top: 0;
  }
  .Superops-TextField__Input {
    line-height: 40px !important;
    font-weight: 700 important;
    font-size: 16px important;
    border-bottom: none !important;
    padding: 0 40px;
  }
  .Superops-TextField__Backdrop {
    border: none !important;
  }
`;

const Recipients = styled.div`
  display: grid;
  grid-template-columns: 6% 94%;
  align-items: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);

  .value_selector__control {
    border-bottom: none !important;
  }
  .value_selector__control--is-focused {
    border-bottom: none !important;
  }
  .value_selector__control--menu-is-open {
    border-bottom: none !important;
  }
`;

export default withErrorBoundary(injectIntl(SendEmailModal));
